'use client';

import { ClientOnly } from '@/components/ClientOnly';
import { PencilIcon } from '@/components/Dashboard/icons/PencilIcon';
import { MaximizeIcon } from '@/components/DetailedView/icons/MaximizeIcon';
import { Share } from '@/components/DetailedView/Share';
import { NotShowable } from '@/components/Metrics/MetricCard/NotShowable';
import RichText from '@/components/RichText';
import { Skeleton } from '@/components/Skeleton';
import {
  SEARCH_CONSTANTS,
  SHARE_STATISTICS_PATH,
  STATISTICS_PATH,
} from '@/constants';
import { PowerBi } from '@/features/powerbi/PowerBi';
import { formatDate } from '@/lib/utils/date';
import { getClientSideURL } from '@/lib/utils/getURL';
import { isMedia } from '@/payload/lib/isMedia';
import { type Statistic } from '@/payload/payload.types';
import { Button } from '@tallinn-arvudes/ui/tallinn-design/Button';
import { Chip } from '@tallinn-arvudes/ui/tallinn-design/Chip';
import { Link } from '@tallinn-arvudes/ui/tallinn-design/Link';
import { cn } from '@tallinn-arvudes/ui/utils';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { type ReactNode, useRef } from 'react';
import { Notification, NotificationProvider } from '../Notification';

const InfoBlock = ({
  children,
  title,
}: {
  title: ReactNode;
  children: ReactNode;
}) => {
  return (
    <div>
      <p className="text-semantic-content-subtle text-[1rem]">{title}</p>
      {children}
    </div>
  );
};

export const StatisticDetailedView = ({
  data,
  inSideModal,
  loggedIn,
}: {
  data: Statistic;
  loggedIn: boolean;
  inSideModal?: boolean;
}) => {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const isEmbeddable = data.type === 'iframe' || data.type === 'powerbi';
  let component: ReactNode = null;

  if (data.type === 'external') {
    component = <NotShowable data={data} />;
  } else if (data.type === 'powerbi') {
    component = <PowerBi data={data} />;
  } else if (data.type === 'iframe' && data.url) {
    component = (
      <iframe
        title={data.titlePlainText || 'Nimetu statistika'}
        className="h-full w-full"
        src={data.url}
      />
    );
  }

  const media = isMedia(data.image);

  return (
    <NotificationProvider>
      <div
        ref={containerRef}
        className="relative h-full flex flex-col gap-6 lg:gap-12 grow"
      >
        <Notification
          successClassName={'xl:w-3/5 whitespace-pre-wrap'}
          isDismissible
        />
        <div className="relative space-y-4 lg:space-y-7 w-full max-w-content md:mx-auto grow">
          <div className="px-4 lg:px-10 pt-5 lg:pt-10">
            <div className="text-semantic-content-brand space-y-3">
              <sub className="uppercase text-xs text-semantic-content-brand">
                STATISTIKA
              </sub>
              <h1 className="tds-heading-lg" title={data.titlePlainText || ''}>
                <RichText data={data.title!} />
              </h1>
            </div>
            <div
              className={cn(
                'absolute top-0 right-0 !mt-4 lg:!mt-8 !mr-6 flex items-center gap-1',
                {
                  '!mr-[4rem] md:!mr-6': inSideModal,
                },
              )}
            >
              {loggedIn && (
                <Button
                  compact
                  variant="icon"
                  tooltip="Redigeeri"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-semantic-content-faint"
                  href={`/admin/collections/statistics/${data.id}`}
                >
                  <PencilIcon className="!w-5 lg:!w-6 !h-5 lg:!h-6" />
                </Button>
              )}
              {inSideModal && (
                <Button
                  compact
                  variant="icon"
                  tooltip="Suurenda"
                  className="text-semantic-content-faint"
                  onClick={() => {
                    window.location.replace(`${STATISTICS_PATH}/${data.id}`);
                  }}
                >
                  <MaximizeIcon className="!w-5 lg:!w-6 !h-5 lg:!h-6" />
                </Button>
              )}
              <Share
                getIframeProps={
                  isEmbeddable
                    ? () => ({
                        title: data.titlePlainText,
                        src: `${getClientSideURL()}${SHARE_STATISTICS_PATH}/${data.id}`,
                      })
                    : undefined
                }
                containerRef={containerRef}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-7 px-4 lg:px-10">
            <div className="grow h-[300px] md:h-[450px]">{component}</div>
            <div className="flex flex-col gap-2">
              {data.descriptionPlainText && (
                <div className="mt-5 text-semantic-content-subtle leading-8">
                  <RichText data={data.description!} />
                </div>
              )}
              {!!data.labels?.length && (
                <div className="flex gap-2 flex-wrap">
                  {data.labels.map((label) => {
                    const labelObj = typeof label === 'object' ? label : null;
                    if (!labelObj) {
                      return null;
                    }
                    return (
                      <Chip
                        onClick={() => {
                          const url = `${SEARCH_CONSTANTS.SEARCH_BASE_URL}?${SEARCH_CONSTANTS.QUERY_PARAM}=${labelObj.name}`;

                          if (inSideModal) {
                            window.location.href = url;
                            return;
                          }

                          router.push(url);
                        }}
                        key={labelObj.id}
                      >
                        {labelObj.name}
                      </Chip>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bg-semantic-surface-faint flex justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-7 px-4 lg:px-10 py-5 w-full max-w-content">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-4 h-fit">
              {data.dataSource && (
                <InfoBlock title="Allikas">
                  <p>{data.dataSource}</p>
                </InfoBlock>
              )}
              <InfoBlock title="Viimati muudetud">
                <ClientOnly
                  fallback={<Skeleton isLoading className="h-5 mt-1 w-44" />}
                >
                  <p>{formatDate(new Date(data.updatedAt))}</p>
                </ClientOnly>
              </InfoBlock>
              {data.responsibleInstitutionName && (
                <InfoBlock title="Haldaja">
                  {data.responsibleInstitutionLink ? (
                    <Link external href={data.responsibleInstitutionLink}>
                      {data.responsibleInstitutionName}
                    </Link>
                  ) : (
                    <p>{data.responsibleInstitutionName}</p>
                  )}
                </InfoBlock>
              )}
              {data.monitoringPeriod && (
                <InfoBlock title="Uuendamise intervall">
                  <p>{data.monitoringPeriod}</p>
                </InfoBlock>
              )}
              {data.code && (
                <InfoBlock title="ID">
                  <p>{data.code}</p>
                </InfoBlock>
              )}
            </div>
            <div className="flex gap-4 md:gap-10 flex-col md:flex-row justify-between">
              {data.methodologyPlainText && (
                <InfoBlock title="Metoodika">
                  <RichText className="leading-7" data={data.methodology!} />
                </InfoBlock>
              )}
              {media?.url && (
                <div className="shrink-0 lg:ml-auto">
                  <Image
                    src={media.url}
                    alt={media.alt ?? ''}
                    width={media.width || 80}
                    height={media.height || 80}
                    priority
                    loading="eager"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </NotificationProvider>
  );
};
